import React, { useState, useEffect } from 'react';
import {
    TextField, Button, Grid, FormControl, InputLabel, Select, MenuItem, SelectChangeEvent,
    Autocomplete, Typography, Box
} from '@mui/material';
import { Equipment } from "../types/equipmentTypes";
import { Employee } from "../types/EmployeeTypes";
import { CategoryEquipament } from '../types/categoryEquipaments';
import { Project } from '../types/projectTypes';
import { getHistoricalFronts, HistoricalFront, getEquipmentByInternalCode } from '../api/equipmentApi';

interface EquipmentFormProps {
    projects: Project[];
    employees: Employee[];
    categoryEquipmentList: CategoryEquipament[];
    onAddEquipment: (equipment: Partial<Equipment>, workFrontId?: number) => Promise<void>;
    reportDate?: string;
    idProject?: number | null;
    setSelectedProject: (x: boolean) => void;
    setIdProject: (x: number) => void;
}

const EquipmentForm: React.FC<EquipmentFormProps> = ({
    projects, employees, categoryEquipmentList, onAddEquipment,
    reportDate, idProject, setSelectedProject, setIdProject
}) => {
    const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
    const [specialProject, setSpecialProject] = useState<number | null>(null);

    // Estado para "Equipo Existente" (Bloqueo en histórico)
    const [isExistingEquipment, setIsExistingEquipment] = useState(false);

    const [formData, setFormData] = useState<Partial<Equipment>>({
        project: undefined, internalCode: '', vehicleNumber: '', name: '',
        owner: undefined, employee: undefined, rentalCost: 0, observations: ''
    });

    const [formErrors, setFormErrors] = useState({
        internalCode: false, vehicleNumber: false, name: false, owner: false, employee: false, project: false
    });

    const [availableFronts, setAvailableFronts] = useState<HistoricalFront[]>([]);
    const [selectedFrontId, setSelectedFrontId] = useState<number | string>('');

    // 1. CARGAR FRENTES SI HAY FECHA
    useEffect(() => {
        if (reportDate && idProject) {
            getHistoricalFronts(idProject, reportDate)
                .then(data => setAvailableFronts(data))
                .catch(err => console.error("Error cargando frentes", err));
        } else {
            setAvailableFronts([]); setSelectedFrontId(''); setIsExistingEquipment(false);
        }
    }, [reportDate, idProject]);

    // 2. AUTO-LLENADO POR CÓDIGO INTERNO (Solo en Histórico)
    useEffect(() => {
        const checkEquipmentExistence = async () => {
            if (reportDate && formData.internalCode && formData.internalCode.length > 2) {
                const found = await getEquipmentByInternalCode(formData.internalCode);
                if (found) {
                    setFormData(prev => ({
                        ...prev,
                        vehicleNumber: found.vehicleNumber,
                        name: found.name,
                        owner: found.owner ? 1 : 0,
                        id: found.id
                    }));
                    if (found.employee) setSelectedEmployee(found.employee);
                    setIsExistingEquipment(true);
                } else {
                    setIsExistingEquipment(false);
                    setFormData(prev => ({ ...prev, id: undefined }));
                }
            }
        };
        const timeoutId = setTimeout(() => { checkEquipmentExistence(); }, 500);
        return () => clearTimeout(timeoutId);
    }, [formData.internalCode, reportDate]);

    const handleInputChange = (field: keyof Equipment, value: any) => {
        if (isExistingEquipment && reportDate && ['vehicleNumber', 'name', 'owner'].includes(field)) return;
        setFormData((prev) => ({ ...prev, [field]: value }));
        setFormErrors((prev) => ({ ...prev, [field]: !value }));
    };

    const handleProjectChange = (e: SelectChangeEvent<number>) => {
        if (reportDate) return;
        const projectId = Number(e.target.value);
        if (projectId === -1) {
            setFormData({ ...formData, project: undefined });
            setSpecialProject(projectId);
            setFormErrors(prev => ({ ...prev, project: false }));
            return;
        }
        const selected = projects.find((p) => p.id === projectId);
        if (selected) {
            setIdProject(projectId);
            setSelectedProject(true);
            setFormData({ ...formData, project: selected });
            setSpecialProject(null);
            setFormErrors(prev => ({ ...prev, project: false }));
        }
    };

    const handleEmployeeChange = (newValue: Employee | null) => {
        setSelectedEmployee(newValue);
        setFormErrors(prev => ({ ...prev, employee: !newValue }));
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const errors = {
            internalCode: !formData.internalCode,
            vehicleNumber: !formData.vehicleNumber,
            name: !formData.name,
            owner: formData.owner === undefined || formData.owner === null,
            employee: selectedEmployee === null && (!specialProject && !!formData.project),
            project: !reportDate && ((!formData.project && specialProject === null) || (formData.owner === 0 && !formData.project)),
        };
        setFormErrors(errors);
        if (Object.values(errors).some((error) => error)) return;

        if (reportDate && !selectedFrontId) {
            alert("Debe seleccionar un Frente de Trabajo");
            return;
        }

        const equipmentToSave = {
            ...formData,
            employee: selectedEmployee?.name === 'Externo / No Aplica' ? null : selectedEmployee,
            active: true
        };

        try {
            await onAddEquipment(equipmentToSave, reportDate ? Number(selectedFrontId) : undefined);

            // Limpiar campos solo si tuvo éxito
            setFormData({
                internalCode: '', vehicleNumber: '', name: '', owner: undefined,
                rentalCost: 0, observations: '', project: undefined, id: undefined
            });
            setSelectedEmployee(null);
            setSpecialProject(null);
            setIsExistingEquipment(false);
            setSelectedFrontId(''); // <--- CORREGIDO AQUÍ (Antes decía etSelectedFrontId)

        } catch (error) {
            console.log("Error controlado en el formulario, no se limpian los campos.");
        }
    };

    const currencyFormatter = new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', minimumFractionDigits: 0 });

    return (
        <Box sx={{ p: 2, backgroundColor: '#f9f9f9', borderRadius: 2, border: '1px solid #ddd' }}>
            {reportDate && (
                <Typography variant="subtitle1" sx={{ color: 'primary.main', fontWeight: 'bold', mb: 2 }}>
                    MODO HISTÓRICO: Editando reporte del {reportDate}
                </Typography>
            )}
            <form onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                    {reportDate && (
                        <Grid item xs={12}>
                            <FormControl fullWidth color="primary" required>
                                <InputLabel>Asignar a Frente de Trabajo (Requerido)</InputLabel>
                                <Select value={selectedFrontId} label="Asignar a Frente de Trabajo (Requerido)" onChange={(e) => setSelectedFrontId(e.target.value)}>
                                    {availableFronts.map((front) => (<MenuItem key={front.id} value={front.id}>{front.label}</MenuItem>))}
                                </Select>
                            </FormControl>
                        </Grid>
                    )}
                    <Grid item xs={12} md={2}>
                        <FormControl fullWidth variant="outlined" error={formErrors.project}>
                            <InputLabel>Proyecto</InputLabel>
                            <Select value={specialProject ?? formData.project?.id ?? ''} onChange={handleProjectChange} label="Proyecto" disabled={!!reportDate} required={!reportDate}>
                                <MenuItem value={-1}>Mantenimiento / Faena</MenuItem>
                                {projects.map((project) => (<MenuItem key={project.id} value={project.id}>{project.costCenter} - {project.name}</MenuItem>))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <TextField
                            label="Codigo Interno" value={formData.internalCode || ''}
                            onChange={(e) => handleInputChange('internalCode', e.target.value)}
                            variant="outlined" fullWidth error={formErrors.internalCode}
                            helperText={isExistingEquipment ? "Equipo encontrado" : ""}
                            color={isExistingEquipment ? "success" : "primary"}
                            focused={isExistingEquipment}
                        />
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <TextField
                            label="Patente" value={formData.vehicleNumber || ''}
                            onChange={(e) => handleInputChange('vehicleNumber', e.target.value)}
                            variant="outlined" fullWidth error={formErrors.vehicleNumber}
                            disabled={isExistingEquipment && !!reportDate}
                            sx={{ backgroundColor: (isExistingEquipment && !!reportDate) ? '#e0e0e0' : 'inherit' }}
                        />
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <FormControl fullWidth variant="outlined" error={formErrors.name}>
                            <InputLabel>Equipo</InputLabel>
                            <Select
                                value={formData.name || ''}
                                onChange={(e) => handleInputChange('name', e.target.value)}
                                label="Equipo"
                                disabled={isExistingEquipment && !!reportDate}
                                sx={{ backgroundColor: (isExistingEquipment && !!reportDate) ? '#e0e0e0' : 'inherit' }}
                            >
                                {categoryEquipmentList.map((category) => (<MenuItem key={category.id} value={category.description}>{category.description}</MenuItem>))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <FormControl variant="outlined" fullWidth error={formErrors.owner}>
                            <InputLabel>Propiedad</InputLabel>
                            <Select
                                value={formData.owner ?? ''}
                                onChange={(e) => handleInputChange('owner', Number(e.target.value))}
                                label="Propiedad"
                                disabled={isExistingEquipment && !!reportDate}
                                sx={{ backgroundColor: (isExistingEquipment && !!reportDate) ? '#e0e0e0' : 'inherit' }}
                            >
                                <MenuItem value={0}>Rentado</MenuItem> <MenuItem value={1}>Propio</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <Autocomplete
                            options={[...employees, { id: -1, rut: '', name: 'Externo / No Aplica', firstName: '', lastName: '' }]}
                            getOptionLabel={(option) => option.name === 'Externo / No Aplica' ? option.name : `${option.rut} ${option.name} ${option.firstName} ${option.lastName}`}
                            renderInput={(params) => <TextField {...params} label="Responsable" placeholder="Buscar por RUT o nombre" error={formErrors.employee} />}
                            onChange={(event, newValue) => handleEmployeeChange(newValue)}
                            value={selectedEmployee || null}
                            autoHighlight
                            noOptionsText="Sin coincidencias"
                            disabled={isExistingEquipment && !!reportDate}
                        />
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <TextField
                            label="Costo Arriendo"
                            value={currencyFormatter.format(formData.rentalCost || 0)}
                            onChange={(e) => { const val = parseInt(e.target.value.replace(/[^0-9]/g, ""), 10); handleInputChange('rentalCost', isNaN(val) ? 0 : val); }}
                            fullWidth disabled={isExistingEquipment && !!reportDate}
                        />
                    </Grid>
                    <Grid item xs={12} md={10}>
                        <TextField label="Observaciones" value={formData.observations || ''} onChange={(e) => handleInputChange('observations', e.target.value)} fullWidth />
                    </Grid>
                    <Grid item xs={12} md={12}>
                        <Button type="submit" variant="contained" color="primary" fullWidth>{reportDate ? "Agregar al Histórico" : "Agregar"}</Button>
                    </Grid>
                </Grid>
            </form>
        </Box>
    );
};
export default EquipmentForm;